import React, { useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import PinDigitsInput from './PinDigitsInput';
import { PIN_LENGTH } from './pinFeatureFlag';
import { pinRuleViolation } from './pinRules';

// Create a PIN (first setup) or choose a new one (forgotten PIN), after the phone was verified by
// OTP. The rules are checked as the user types; the backend checks them again.
export default function PinCreateStep({ mode, phoneNumber, serverError, onSubmit }) {
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const ruleError = pin.length === PIN_LENGTH ? pinRuleViolation(pin, phoneNumber) : null;
  const mismatch = confirm.length === PIN_LENGTH && confirm !== pin;
  const canSubmit = pin.length === PIN_LENGTH && !ruleError && confirm === pin;

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (canSubmit) {
      onSubmit(pin, confirm);
    }
  };

  const isReset = mode === 'PIN_RESET';

  return (
    <Stack component="form" onSubmit={handleSubmit} spacing={3} data-testid="login-pin-create-step">
      <Stack spacing={0.5} alignItems="center" textAlign="center">
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {isReset ? 'Choose a new PIN' : 'Create your PIN'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isReset
            ? 'Your new PIN signs you out of every other device.'
            : `From now on you sign in with your phone number and this ${PIN_LENGTH}-digit PIN.`}
        </Typography>
      </Stack>

      <Stack spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {isReset ? 'New PIN' : 'PIN'}
        </Typography>
        <PinDigitsInput value={pin} onChange={setPin} idPrefix="login-pin-create" label="New PIN"
          error={Boolean(ruleError)} autoFocus />
        <Typography variant="caption" color={ruleError ? 'error.main' : 'text.secondary'}
          data-testid="login-pin-create-hint">
          {ruleError || `${PIN_LENGTH} digits. Avoid repeats, runs like 123456, and your phone number.`}
        </Typography>
      </Stack>

      <Stack spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Confirm PIN
        </Typography>
        <PinDigitsInput value={confirm} onChange={setConfirm} idPrefix="login-pin-confirm" label="Confirm PIN"
          error={mismatch} />
        {mismatch || (submitted && confirm !== pin) ? (
          <Typography variant="caption" color="error.main" data-testid="login-pin-confirm-error">
            The two PINs don&apos;t match.
          </Typography>
        ) : null}
      </Stack>

      {serverError ? (
        <Typography variant="body2" color="error.main" data-testid="login-pin-create-error">
          {serverError}
        </Typography>
      ) : null}

      <Button type="submit" variant="contained" size="large" disabled={!canSubmit}
        data-testid="login-pin-create-submit">
        {isReset ? 'Save new PIN and sign in' : 'Save PIN and sign in'}
      </Button>
    </Stack>
  );
}
